import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Tooltip,
  Avatar,
  CircularProgress,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import VideocamIcon from '@mui/icons-material/Videocam';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import { useChatContext } from '../context/SignamRContext';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import ReplyIcon from '@mui/icons-material/Reply';

const WA_GREEN = '#ff8f28';
const WA_GREEN_HOVER = '#fd8412ff';

const MessageInput = ({
  activeChat,
  isEditMsg,
  setIsEditMsg,
  msgForEdit,
  setMsgForEdit,
  replyTo,
  setReplyTo,
}) => {
  const [value, setValue] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const { setMessages } = useChatContext();
  const { t, i18n } = useTranslation();
  const apiBaseUrl = process.env.REACT_APP_API_CHAT_PROD;

  useEffect(() => {
    if (isEditMsg) {
      setValue(msgForEdit?.body);
    }
  }, [isEditMsg]);

  const uploadFiles = async () => {
    setLoading(true);
    try {
      await Promise.all(
        files.map(async (file) => {
          const formData = new FormData();
          formData.append('fajl', file);
          formData.append('userId', localStorage.getItem('idKorisnika') || '');

          const res = await axios.post(
            `${apiBaseUrl}/Messages/create_image`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          );

          const imageUrl = res.data?.url;

          if (imageUrl) {
            await axios.post(`${apiBaseUrl}/Messages/create`, {
              conversationId: activeChat?.id,
              message: '',
              image: imageUrl,
              userId: +localStorage.getItem('idKorisnika'),
              id_kategorije: +localStorage.getItem('id_kategorije'),
              id_dokumenta: +localStorage.getItem('id_dokumenta'),
              idKorisnika: +localStorage.getItem('idKorisnika'),
              oznaka: String(localStorage.getItem('oznaka')) || 'null',
            });
          }
        }),
      );
    } catch (error) {
      console.error('Greška pri uploadu fajlova:', error);
    } finally {
      setFiles([]);
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const trimmed = value.trim();
    if (!trimmed && files.length === 0) return;

    if (files.length > 0) {
      await uploadFiles();
    }

    if (trimmed) {
      const endpoint = isEditMsg
        ? `${apiBaseUrl}/Messages/update_message`
        : `${apiBaseUrl}/Messages/create`;

      const payload = {
        conversationId: activeChat?.id,
        message: trimmed,
        userId: +localStorage.getItem('idKorisnika'),
        id_kategorije: +localStorage.getItem('id_kategorije'),
        id_dokumenta: +localStorage.getItem('id_dokumenta'),
        idKorisnika: +localStorage.getItem('idKorisnika'),
        oznaka: String(localStorage.getItem('oznaka')) || 'null',
      };

      if (replyTo) {
        payload.idParent = replyTo.id;
        payload.parentBody = replyTo.body || null;
        payload.parentImage = replyTo.image || null;
      }

      // Ako praviš novu poruku, po potrebi dodaj i id za edit
      if (isEditMsg && msgForEdit?.id) {
        payload.messageId = msgForEdit.id;
      }

      try {
        const response = await axios.post(endpoint, payload);
        if (response.status === 200) {
          setValue('');
          setIsEditMsg(false);
          setMsgForEdit(null);
          setReplyTo(null);
        }
      } catch (err) {
        console.error(
          `Greška prilikom ${isEditMsg ? 'izmene' : 'slanja'} poruke:`,
          err,
        );
      }
    }
  };

  const handleFilePick = (accept, capture, multiple) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    if (capture) input.capture = capture;
    if (multiple) input.multiple = true;
    input.onchange = (e) => {
      const selectedFiles = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...selectedFiles]);
    };
    input.click();
  };

  const handlePasteFiles = (event) => {
    const clipboardItems = event.clipboardData?.items || [];
    const pastedImages = [];

    for (let i = 0; i < clipboardItems.length; i += 1) {
      const item = clipboardItems[i];
      if (item.kind === 'file' && item.type?.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          const extension = file.type.split('/')[1] || 'png';
          const normalizedFile = new File(
            [file],
            file.name || `pasted-image-${Date.now()}.${extension}`,
            { type: file.type },
          );
          pastedImages.push(normalizedFile);
        }
      }
    }

    if (pastedImages.length > 0) {
      event.preventDefault();
      setFiles((prev) => [...prev, ...pastedImages]);
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 1.5,
        // borderRadius: 2,
        bgcolor: 'white',
        mb: 0,
        gap: 1,
        justifyContent: 'center', // centriranje sadržaja po y-osi
        minHeight: '76px',
      }}
    >
      {files.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            maxWidth: '100%',
          }}
        >
          {files.map((file, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                width: 56,
                height: 56,
              }}
            >
              <Tooltip title={file.name}>
                <Avatar
                  variant="rounded"
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  sx={{ width: 56, height: 56 }}
                />
              </Tooltip>

              {/* X dugme za uklanjanje */}
              <IconButton
                size="small"
                onClick={() =>
                  setFiles((prev) => prev.filter((_, i) => i !== index))
                }
                sx={{
                  position: 'absolute',
                  top: -8,
                  left: -8,
                  bgcolor: 'white',
                  boxShadow: 1,
                  '&:hover': { bgcolor: '#f0f0f0' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      {isEditMsg && msgForEdit && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: '#f1f1f1',
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
            mb: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: '0.8rem', color: '#555', flex: 1 }}
          >
            ✏️ Izmena poruke: {msgForEdit.body}
          </Typography>
          <IconButton
            size="small"
            onClick={() => {
              setIsEditMsg(false);
              setMsgForEdit(null);
              setValue('');
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {replyTo && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: '#f0f0f0',
            borderLeft: '3px solid #ff8f28',
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
            mb: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.8rem', fontWeight: 600 }}
            >
              <ReplyIcon
                sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }}
              />
              {t('replyingTo')} {replyTo.sender?.name || 'Korisnik'}
            </Typography>
            {replyTo.body && (
              <Typography
                variant="body2"
                sx={{ fontSize: '0.75rem', color: '#666' }}
              >
                {replyTo.body.length > 60
                  ? `${replyTo.body.slice(0, 60)}…`
                  : replyTo.body}
              </Typography>
            )}
            {replyTo.image && (
              <img
                src={replyTo.image}
                alt=""
                style={{ width: 50, height: 50, borderRadius: 4, marginTop: 4 }}
              />
            )}
          </Box>
          <IconButton size="small" onClick={() => setReplyTo(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
        }}
      >
        {/* FAB meni inline */}
        {/* <Box sx={{ position: 'relative' }}>
          <SpeedDial
            ariaLabel="Dodaj fajl"
            direction="up"
            icon={<SpeedDialIcon />}
            FabProps={{
              size: 'small',
              sx: {
                bgcolor: WA_GREEN,
                '&:hover': { bgcolor: WA_GREEN_HOVER },
                width: 40,
                height: 40,
                minHeight: 40,
              },
            }}
            sx={{
              position: 'absolute',
              bottom: '-20px',
            }}
          >
            <SpeedDialAction
              key="Slika"
              icon={<CameraAltIcon />}
              tooltipTitle="Slika"
              onClick={() => handleFilePick('image/*', 'environment', false)}
            />
            <SpeedDialAction
              key="Video"
              icon={<VideocamIcon />}
              tooltipTitle="Video"
              onClick={() => handleFilePick('video/*', 'environment', false)}
            />
            <SpeedDialAction
              key="Dokument"
              icon={<DescriptionIcon />}
              tooltipTitle="Dokument"
              onClick={() =>
                handleFilePick(
                  'application/pdf,application/msword',
                  undefined,
                  true,
                )
              }
            />
          </SpeedDial>
        </Box> */}

        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={() =>
              handleFilePick(
                'image/*,video/*,application/pdf,application/msword',
                undefined,
                true,
              )
            }
            sx={{
              bgcolor: WA_GREEN,
              '&:hover': { bgcolor: WA_GREEN_HOVER },
              width: 40,
              height: 40,
              minHeight: 40,
              color: '#fff',
            }}
          >
            <DescriptionIcon />
          </IconButton>
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={5}
          placeholder={t('inputMessage')}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPaste={handlePasteFiles}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
          sx={{
            flex: 1,
            // marginLeft: '58px',
            '& .MuiInputBase-root': {
              borderRadius: 2,
              overflow: 'auto',
              alignItems: 'flex-start',
              padding: '14px 17px',
            },
          }}
        />

        <IconButton
          onClick={handleSend}
          disabled={loading}
          sx={{
            bgcolor: WA_GREEN,
            color: '#fff',
            alignSelf: 'center', // uvek prati dno TextField-a
            '&:hover': { bgcolor: WA_GREEN_HOVER },
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          ) : (
            <SendIcon />
          )}
        </IconButton>
      </Box>
    </Paper>
  );
};

export default MessageInput;
